(() => {
  'use strict';

  /* ---------------------------------------------------------------- */
  /* Header: transparent over a dark hero, solid once scrolled         */
  /* ---------------------------------------------------------------- */

  function syncHeaderHeight() {
    const wrapper = document.querySelector('.header-wrapper');
    if (!wrapper) return;
    const set = () => {
      const h = wrapper.getBoundingClientRect().height;
      if (h > 0) document.documentElement.style.setProperty('--bald-header-h', h + 'px');
    };
    set();
    window.addEventListener('resize', set);
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(set).catch(() => {});
    }
  }

  function initHeaderTint() {
    const hero = document.querySelector('[data-bald-transparent-hero]');
    if (!hero) return;
    document.body.setAttribute('data-bald-transparent-header', '');
    syncHeaderHeight();

    const setSolid = () => {
      const solid = window.scrollY > 24;
      document.body.toggleAttribute('data-bald-header-solid', solid);
    };
    setSolid();
    window.addEventListener('scroll', setSolid, { passive: true });
  }

  /* ---------------------------------------------------------------- */
  /* Before/after band: the fallback for the scroll-tied wipe           */
  /* ---------------------------------------------------------------- */

  /* The wipe is CSS where the browser has scroll timelines
     (bald-texture-band.css): it then moves in step with the scroll itself.
     This script only drives it where CSS cannot — older browsers (Safari
     before 26), the theme editor, and the bald-no-sda test hook — using the
     same two transforms, so the look is identical.

     It used to drive every browser, and on phones it was choppy (Samuel,
     2026-10-04): each scroll event measured the band and rewrote a clip-path
     and the seam's `left`, so the phone re-laid-out and repainted the whole
     photo on every event, and on an iPhone the script runs behind the scroll
     besides. Transforms only move layers that are already painted, and the
     measuring now happens once per frame at most. */
  function initTextureBand() {
    let destroy = mountTextureBand(document);
    if (!(window.Shopify && window.Shopify.designMode)) return;

    const ours = (e) => e.target.querySelector && e.target.querySelector('[data-bald-band]');
    document.addEventListener('shopify:section:unload', (e) => {
      if (destroy && ours(e)) { destroy(); destroy = null; }
    });
    document.addEventListener('shopify:section:load', (e) => {
      if (!ours(e)) return;
      if (destroy) destroy();
      destroy = mountTextureBand(e.target);
    });
  }

  function mountTextureBand(root) {
    const band = root.querySelector('[data-bald-band]');
    if (!band) return null;
    const top = band.querySelector('[data-bald-band-top]');
    const inner = band.querySelector('[data-bald-band-inner]');
    if (!top || !inner) return null;

    const html = document.documentElement;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
    const wide = window.matchMedia('(min-width: 750px)');
    const timelines = !!(window.CSS && CSS.supports && CSS.supports('animation-timeline', 'view()'));
    // The same conditions the stylesheet's @supports and selectors test.
    const cssDrives = () => timelines && !reduce.matches && !html.matches('.shopify-design-mode, .bald-no-sda');

    // p is how much of the band the "before" photo covers, in percent. The
    // layer slides left to uncover the "after"; the photo inside it slides
    // right by as much, so the picture itself stands still.
    const place = (p) => {
      top.style.transform = 'translateX(' + (p - 100).toFixed(2) + '%)';
      inner.style.transform = 'translateX(' + (100 - p).toFixed(2) + '%)';
    };

    // Settled when the band comes near and when the motion setting changes,
    // not on every scroll event: where CSS drives the wipe, scrolling runs
    // no script of ours at all.
    let css = cssDrives();
    let queued = false;
    const update = () => {
      queued = false;
      if (css) {
        top.style.transform = '';
        inner.style.transform = '';
        return;
      }
      if (reduce.matches) { place(50); return; }
      // The same ranges and travel as the stylesheet, in a straight line:
      // computers 100% covered to 0% (edge to edge), phones 96% to 8%.
      const rect = band.getBoundingClientRect();
      const vh = window.innerHeight;
      let from, to; // the band's top edge where the wipe starts and ends
      if (wide.matches) {
        from = vh - rect.height * 0.825; // `entry 82.5%`: most of it in view
        to = Math.min(0, vh - rect.height) - rect.height * 0.01; // `exit 1%`: just leaving
      } else {
        from = vh - (vh + rect.height) * 0.12; // `cover 12%`
        to = -rect.height; // `cover 100%`: gone at the top
      }
      const t = Math.max(0, Math.min(1, (from - rect.top) / (from - to)));
      place(wide.matches ? 100 - 100 * t : 96 - 88 * t);
    };
    const request = () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(update);
    };
    const recheck = () => { css = cssDrives(); request(); };

    // Only while the band is near the window.
    let near = !('IntersectionObserver' in window);
    const io = near ? null : new IntersectionObserver((entries) => {
      near = entries[0].isIntersecting;
      if (near) recheck();
    }, { rootMargin: '200px 0px' });
    if (io) io.observe(band);

    const onScroll = () => { if (near && !css) request(); };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', request);
    reduce.addEventListener('change', recheck);
    update();

    return () => {
      if (io) io.disconnect();
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', request);
      reduce.removeEventListener('change', recheck);
    };
  }

  /* ---------------------------------------------------------------- */
  /* Sticky add-to-cart bar                                            */
  /* ---------------------------------------------------------------- */

  function initStickyBar() {
    const bar = document.querySelector('[data-bald-sticky-bar]');
    if (!bar) return;
    const threshold = Number(bar.dataset.baldShowAfter || 560);

    /* The bar's button submits the product page's own form from outside it,
       through the form attribute. The section used to hard-code
       form="product-form-main", but Sense names the form after the section
       ("product-form-template--…__main"), so the button pointed at nothing
       and did nothing. Point it at the form that is actually there. */
    const submit = bar.querySelector('button[type="submit"]');
    const productForm = document.querySelector('form[id^="product-form-"][data-type="add-to-cart-form"]');
    if (submit && productForm) submit.setAttribute('form', productForm.id);

    const update = () => {
      const show = window.scrollY > threshold && window.innerWidth < 860;
      bar.classList.toggle('is-visible', show);
    };
    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
  }

  /* ---------------------------------------------------------------- */
  /* In-page anchors: close the drawer, then scroll smoothly           */
  /* ---------------------------------------------------------------- */

  /* The nav is configured in the Shopify admin with absolute URLs
     (https://baldstyle.sk/#zlozenie), not bare "#zlozenie", so the browser
     treats a click as a navigation rather than an anchor jump. On mobile
     that left the drawer sitting open over the section it had just moved
     to. Match on the resolved path + hash instead of the href's shape, so
     the same handler works on the live domain and on the dev server. */

  /* Matches closeAnimation() in global.js — scrolling before the drawer
     has cleared the viewport plays the whole animation behind the overlay. */
  const DRAWER_CLOSE_MS = 400;

  function anchorTarget(hash) {
    if (!hash || hash === '#') return null;
    try {
      return document.getElementById(decodeURIComponent(hash.slice(1)));
    } catch (e) {
      return null;
    }
  }

  function scrollToAnchor(target, smooth) {
    /* Where the target should land depends on the header mode
       (sticky_header_type in header-group.json):

       - Pinned ("always", "reduce-logo-size"): the header covers the top of
         the window, so offset by its height, measured now — it changes with
         the logo across breakpoints.
       - "on-scroll-up", this shop's mode: the header slides away as the page
         scrolls down, so only the 12px margin counts. Offsetting by the
         header anyway left a header-sized empty band above every section. A
         jump UP would normally make Sense bring the header back over the
         target, so it is told to keep it hidden for this one scroll, through
         its own preventHeaderReveal hook.

       Either way the target ends 12px below the visible header's lower edge,
       which is what buy-e2e.js measures. */
    const sticky = document.querySelector('sticky-header');
    const mode = sticky ? sticky.dataset.stickyType : 'none';
    const pinned = mode === 'always' || mode === 'reduce-logo-size';
    const header = document.querySelector('.header-wrapper');
    const offset = (pinned && header ? header.getBoundingClientRect().height : 0) + 12;
    const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const top = target.getBoundingClientRect().top + window.pageYOffset - offset;

    if (!pinned && sticky && top < window.pageYOffset) {
      sticky.dispatchEvent(new Event('preventHeaderReveal'));
    }

    /* An open desktop dropdown sets Sense's preventHide, which would leave
       the header sitting over the section just scrolled to. */
    document.querySelectorAll('header-menu details[open]').forEach((d) => d.removeAttribute('open'));

    window.scrollTo({ top: Math.max(top, 0), behavior: smooth && !reduce ? 'smooth' : 'auto' });

    /* Keyboard users should land in the section, not back at the top of
       the document — this is also what makes the skip link work. */
    if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });
  }

  /* Does this link lead to a place on the page we are on?

     The plain case is a link to this page's own address. The other is the
     menu: its links point at the home page's sections (/#zlozenie), and the
     product page is built from the same sections under the same ids. There a
     menu link scrolls to this page's copy; without this it left for the home
     page, and a visitor reading about the product lost their place. A link to
     a home-page section this page does not have still navigates. */
  function pointsHere(url) {
    if (url.pathname === window.location.pathname) return true;
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return url.pathname === root && !!anchorTarget(url.hash);
  }

  function initAnchorScroll() {
    document.addEventListener('click', (event) => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey) return;

      const link = event.target.closest && event.target.closest('a[href]');
      if (!link || link.hasAttribute('download') || link.target === '_blank') return;

      let url;
      try {
        url = new URL(link.href, window.location.href);
      } catch (e) {
        return;
      }
      if (!pointsHere(url)) return;

      const target = anchorTarget(url.hash);
      if (!target) return;

      event.preventDefault();

      const drawer = link.closest('header-drawer, menu-drawer');
      const wasOpen = drawer && drawer.querySelector('details[open]');
      if (wasOpen && typeof drawer.closeMenuDrawer === 'function') {
        const summary = drawer.querySelector('summary');
        drawer.closeMenuDrawer(event, summary);

        /* The grey scrim over the page is `.header__icon--menu[aria-expanded="true"]::before`,
           so it is that attribute, not the details[open] state, that keeps it on
           screen. Sense only resets aria-expanded for KeyboardEvents:

             if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false)

           We close on a click, so without this the scrim stayed over the whole
           page after tapping a menu link and swallowed every tap. Setting it is
           also just correct: the control really is collapsed now. */
        if (summary) summary.setAttribute('aria-expanded', 'false');
      }

      window.setTimeout(
        () => {
          scrollToAnchor(target, true);
          if (window.history && window.history.pushState) {
            window.history.pushState(null, '', url.hash);
          }
        },
        wasOpen ? DRAWER_CLOSE_MS : 0
      );
    });

    /* Arriving from another page with a hash — the browser has already
       jumped, and landed under the sticky header. Correct it once layout
       has settled, without animating a scroll the visitor didn't ask for. */
    const landed = anchorTarget(window.location.hash);
    if (landed) window.setTimeout(() => scrollToAnchor(landed, false), 0);
  }

  /* ---------------------------------------------------------------- */
  /* Cookie consent: everything goes to Shopify's own banner            */
  /* ---------------------------------------------------------------- */

  /* Shopify's consent banner is the only consent UI on the site. Its script
     exposes window.privacyBanner.showPreferences(), which opens the same
     preferences window as the banner's own "manage preferences" button. Every
     link to the cookie-settings page (the footer's "Nastavenia cookies") and
     the button on that page open it in place.

     The page used to carry its own switches. They saved to localStorage and
     pushed to Shopify but never read Shopify back, so they could show a choice
     that was not the one in force. They are gone; so is what they stored.

     If the banner script is not on the page (consent collection switched off
     for the visitor's region, or it failed to load), a link simply goes to
     the page as before, and the page's button says it could not open. */
  const COOKIE_PAGE = '/pages/nastavenia-cookies';

  function openCookiePreferences() {
    const banner = window.privacyBanner;
    if (!banner || typeof banner.showPreferences !== 'function') return false;
    try {
      banner.showPreferences();
      return true;
    } catch (e) {
      return false;
    }
  }

  function initCookiePrefs() {
    try {
      localStorage.removeItem('bald_cookie_prefs');
    } catch (e) {
      /* storage blocked: nothing was stored either */
    }

    document.addEventListener('click', (e) => {
      const trigger = e.target.closest && e.target.closest('[data-bald-cookie-prefs], a[href]');
      if (!trigger) return;

      if (trigger.matches('a[href]')) {
        let url;
        try {
          url = new URL(trigger.getAttribute('href'), window.location.href);
        } catch (err) {
          return;
        }
        if (url.origin !== window.location.origin || url.pathname.replace(/\/$/, '') !== COOKIE_PAGE) return;
        if (openCookiePreferences()) e.preventDefault();
        return;
      }

      if (!openCookiePreferences()) {
        const note = document.querySelector('[data-bald-cookie-fallback]');
        if (note) note.hidden = false;
      }
    });
  }

  /* ---------------------------------------------------------------- */
  /* Cart drawer: replay the free-shipping bar when the drawer opens   */
  /* ---------------------------------------------------------------- */

  function initCartShippingBar() {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer || typeof MutationObserver === 'undefined') return;

    // The bar is server-rendered, so its mount animation has already
    // finished by the time the drawer is first opened. Replaying it on
    // open is what the shopper actually sees. (Quantity changes re-render
    // the node, so those animate on their own.)
    const replay = () => {
      const fill = drawer.querySelector('.bald-cart-shipping__fill');
      if (!fill) return;
      fill.style.animation = 'none';
      void fill.offsetWidth; // force reflow so the animation can restart
      fill.style.animation = '';
    };

    new MutationObserver(() => {
      if (drawer.classList.contains('active')) replay();
    }).observe(drawer, { attributes: true, attributeFilter: ['class'] });
  }

  /* ---------------------------------------------------------------- */
  /* Cart drawer upsell: swap the single "1 kus" line for "2 kusy"     */
  /* ---------------------------------------------------------------- */

  function initCartUpsell() {
    document.body.addEventListener('click', (event) => {
      const btn = event.target.closest('[data-bald-upgrade-pack]');
      if (!btn) return;
      event.preventDefault();
      const fromKey = btn.dataset.baldFromKey;
      const toId = btn.dataset.baldToVariantId;
      if (!fromKey || !toId) return;
      btn.disabled = true;

      /* This used to call cartDrawer.refresh(), which does not exist on the
         element, so every upgrade fell through to a full page reload. Swap the
         line and repaint the drawer from the section HTML instead. The remove
         stays quiet for the same reason as in the pack rebuild: announced on
         its own, the shipping line would read the cart half-swapped. */
      baldRemoveLine(fromKey, { announce: false })
        .then(() => baldAddToCart(toId, 1))
        .catch(() => {
          btn.disabled = false;
          window.location.reload();
        });
    });
  }

  /* ---------------------------------------------------------------- */
  /* Legal page: build the sidebar TOC from the rich-text body's <h2>s */
  /* ---------------------------------------------------------------- */

  function slugify(text) {
    var diacritics = /[̀-ͯ]/g;
    return (
      'lg-' +
      text
        .toLowerCase()
        .normalize('NFD')
        .replace(diacritics, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '')
    );
  }

  function initLegalToc() {
    const body = document.querySelector('[data-bald-legal-body]');
    const list = document.querySelector('[data-bald-legal-toc-list]');
    if (!body || !list) return;

    const headings = body.querySelectorAll('h2');
    if (!headings.length) return;

    const links = [];
    headings.forEach((heading) => {
      if (!heading.id) heading.id = slugify(heading.textContent || '');
      const li = document.createElement('li');
      const a = document.createElement('a');
      a.href = '#' + heading.id;
      a.textContent = heading.textContent;
      li.appendChild(a);
      list.appendChild(li);
      links.push({ heading, link: a });
    });

    /* Measuring every heading is layout work, so it runs at most once a frame
       rather than once per scroll event (a wheel can fire several a frame). */
    let queued = false;
    const setActive = () => {
      queued = false;
      let current = links[0];
      links.forEach((entry) => {
        if (entry.heading.getBoundingClientRect().top - 120 <= 0) current = entry;
      });
      links.forEach((entry) => entry.link.classList.toggle('is-active', entry === current));
    };
    setActive();
    window.addEventListener(
      'scroll',
      () => {
        if (queued) return;
        queued = true;
        requestAnimationFrame(setActive);
      },
      { passive: true }
    );
  }

  /* ---------------------------------------------------------------- */
  /* Rich-text tables: one block per row on phones                     */
  /* ---------------------------------------------------------------- */

  /* The privacy policy's four-column table cannot fit a phone. Below 750px
     the CSS stacks each row into label/value pairs; this supplies the labels
     (each cell gets its column heading as data-label, which the CSS prints
     above it). The text comes from the admin, so the script works from the
     table's own <th> row rather than knowing the columns.

     Changing a table's display drops its table semantics in some browsers
     (Safari), so the roles are set explicitly and a screen reader still hears
     a table. Without this script the table scrolls sideways in its own box. */
  function initRteTables() {
    document.querySelectorAll('.rte table').forEach((table) => {
      const heads = Array.from(table.querySelectorAll('thead th'));
      if (!heads.length) return;
      const labels = heads.map((th) => th.textContent.trim());

      table.setAttribute('role', 'table');
      table.querySelectorAll('thead, tbody').forEach((g) => g.setAttribute('role', 'rowgroup'));
      table.querySelectorAll('tr').forEach((tr) => tr.setAttribute('role', 'row'));
      heads.forEach((th) => th.setAttribute('role', 'columnheader'));
      table.querySelectorAll('tbody tr').forEach((tr) => {
        Array.from(tr.children).forEach((cell, i) => {
          cell.setAttribute('role', 'cell');
          if (labels[i]) cell.dataset.label = labels[i];
        });
      });
      /* An attribute, not a class: Sense styles only table:not([class]), so a
         class would strip the desktop table of its borders. */
      table.setAttribute('data-bald-stack', '');
    });
  }


  /* ---------------------------------------------------------------- */
  /* Reviews: the write-a-review window                                */
  /* ---------------------------------------------------------------- */

  /* The form lives in the page but hidden, and is shown as a window when the
     button under the reviews is pressed. Keeping the markup server-rendered
     means the labels stay editable in the theme editor and nothing has to be
     built by script. */

  function initReviewModal() {
    const modal = document.querySelector('[data-bald-review-modal]');
    if (!modal) return;

    const panel = modal.querySelector('.bald-modal__panel');
    const openers = document.querySelectorAll('[data-bald-review-open]');
    if (!openers.length) return;

    let lastFocused = null;

    /* Used to place initial focus, and by the no-dialog fallback's Tab trap;
       a modal <dialog> traps Tab itself. */
    const focusable = () =>
      Array.from(
        panel.querySelectorAll('a[href], button:not(:disabled), input:not([type="hidden"]), textarea, select, [tabindex]:not([tabindex="-1"])')
      ).filter((el) => el.offsetParent !== null || el === document.activeElement);

    /* showModal() is the whole fix. It moves the sheet into the browser's top
       layer, so no ancestor can reposition it and the page behind is made
       inert without a scroll lock — which is what the soft keyboard kept
       fighting on iOS. Everything the old code did by hand (body locking,
       measuring visualViewport, offsetting the sheet) is gone. */
    const canDialog = typeof modal.showModal === 'function';

    const open = (trigger) => {
      lastFocused = trigger || document.activeElement;

      if (canDialog) {
        if (!modal.open) modal.showModal();
      } else {
        /* Ancient browsers only. Good enough to fill in a form. */
        modal.setAttribute('open', '');
      }

      /* A plain overflow lock, so the page behind does not scroll on desktop
         and Android. Deliberately NOT position:fixed — that is what iOS broke
         on, and with the sheet in the top layer it is no longer needed: iOS
         ignoring overflow:hidden now costs nothing, because a scroll behind
         the sheet cannot move the sheet. */
      document.body.classList.add('bald-modal-open');
      document.documentElement.classList.add('bald-modal-open');

      const first = focusable()[0];
      if (first) first.focus({ preventScroll: true });
    };

    const close = () => {
      if (canDialog) {
        if (modal.open) modal.close();
      } else {
        modal.removeAttribute('open');
      }
      document.body.classList.remove('bald-modal-open');
      document.documentElement.classList.remove('bald-modal-open');
      if (lastFocused && typeof lastFocused.focus === 'function') {
        lastFocused.focus({ preventScroll: true });
      }
    };

    /* A dialog can close without going through close() above — Escape does it
       natively, and so would a form with method="dialog". The close event is
       the one hook that fires every way it can happen, so the tidying up lives
       here rather than in the click handler. */
    modal.addEventListener('close', () => {
      document.body.classList.remove('bald-modal-open');
      document.documentElement.classList.remove('bald-modal-open');
      if (lastFocused && typeof lastFocused.focus === 'function') {
        setTimeout(() => lastFocused.focus({ preventScroll: true }), 0);
      }
    });

    /* Escape fires cancel at once but close only when the browser gets round
       to it — measured at about 300ms in Chrome, during which the dialog was
       already gone but the page behind still would not scroll. Nothing here
       ever vetoes a cancel, so a cancel always ends in a close: unlock now. */
    modal.addEventListener('cancel', () => {
      document.body.classList.remove('bald-modal-open');
      document.documentElement.classList.remove('bald-modal-open');
    });

    /* The sheet is full height and the keyboard covers its lower half, so a
       field in the middle can be focused and still sit behind the keys.
       scrollIntoView is no help here: the browser measures against the layout
       viewport, which the keyboard does not change on iOS. visualViewport
       .height is the honest number for what can actually be seen, so scroll
       the sheet's own scroller by the difference. */
    const revealField = (field) => {
      const scroller = modal.querySelector('.bald-modal__scroll');
      if (!scroller) return;
      const visible = (window.visualViewport && window.visualViewport.height) || window.innerHeight;
      const margin = 24;
      const box = field.getBoundingClientRect();
      if (box.bottom > visible - margin) {
        scroller.scrollTop += box.bottom - visible + margin;
      } else if (box.top < margin) {
        scroller.scrollTop -= margin - box.top;
      }
    };

    panel.addEventListener('focusin', (event) => {
      const field = event.target;
      if (!field.matches || !field.matches('input, textarea, select')) return;
      /* Twice: once now, once after the keyboard animation has finished and
         visualViewport reports its new height. */
      revealField(field);
      setTimeout(() => { if (modal.open) revealField(field); }, 350);
    });

    /* The keyboard opening or closing changes what is visible, so re-check
       whatever happens to be focused at that moment. */
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', () => {
        if (!modal.open) return;
        const active = document.activeElement;
        if (active && panel.contains(active) && active.matches('input, textarea, select')) {
          revealField(active);
        }
      });
    }

    openers.forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        open(button);
      });
    });

    modal.querySelectorAll('[data-bald-modal-close], [data-bald-modal-veil]').forEach((el) => {
      el.addEventListener('click', close);
    });

    document.addEventListener('keydown', (event) => {
      if (!modal.open) return;

      /* A modal <dialog> traps Tab and handles Escape itself, so everything
         below is only for the no-dialog fallback. Running both would fight. */
      if (canDialog) return;

      if (event.key === 'Escape') {
        close();
        return;
      }

      /* Keep Tab inside the window while it is open, or focus wanders onto the
         page behind it, which a screen reader cannot see out of. */
      if (event.key === 'Tab') {
        const items = focusable();
        if (!items.length) return;
        const first = items[0];
        const last = items[items.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    });

    /* Deep link support: /#napis-recenziu opens the window rather than
       scrolling to something that is not on the page. */
    if (window.location.hash === '#napis-recenziu') open(null);
  }

  /* ---------------------------------------------------------------- */
  /* Reviews: paging through the grid                                  */
  /* ---------------------------------------------------------------- */

  /* Every approved review is already in the page, so paging is just showing
     and hiding. No reload, no request, and it still works if the review
     service is unreachable. */

  function initReviewPager() {
    const grid = document.querySelector('[data-bald-reviews-grid]');
    const pager = document.querySelector('[data-bald-reviews-pager]');
    if (!grid || !pager) return;

    const perPage = Math.max(1, Number(grid.dataset.perPage) || 6);
    const label = pager.querySelector('[data-bald-page-label]');
    const prev = pager.querySelector('[data-bald-page-prev]');
    const next = pager.querySelector('[data-bald-page-next]');
    let page = 0;

    const cards = () => Array.from(grid.querySelectorAll('.bald-reviews__card'));

    const empty = document.querySelector('[data-bald-reviews-empty]');

    const render = () => {
      const all = cards();

      /* The grid and the pager are always in the page, even with nothing in
         them, so the top-up has somewhere to append to. This is what keeps the
         "no reviews yet" line honest. */
      if (empty) empty.hidden = all.length > 0;

      const pages = Math.max(1, Math.ceil(all.length / perPage));
      if (page > pages - 1) page = pages - 1;

      all.forEach((card, index) => {
        card.hidden = Math.floor(index / perPage) !== page;
      });

      pager.hidden = pages < 2;
      if (label) label.textContent = `${page + 1} / ${pages}`;
      if (prev) prev.disabled = page === 0;
      if (next) next.disabled = page >= pages - 1;
    };

    const go = (delta) => {
      page += delta;
      render();
      /* Bring the top of the list back into view, otherwise page two starts
         halfway down the screen. */
      const top = grid.getBoundingClientRect().top + window.pageYOffset - 120;
      const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      window.scrollTo({ top: Math.max(top, 0), behavior: reduce ? 'auto' : 'smooth' });
    };

    if (prev) prev.addEventListener('click', () => go(-1));
    if (next) next.addEventListener('click', () => go(1));

    render();

    /* The background top-up appends cards after this has run. */
    document.addEventListener('bald:reviews-added', render);
  }

  /* ---------------------------------------------------------------- */
  /* Reviews: submitting the form                                      */
  /* ---------------------------------------------------------------- */

  function initReviewForm() {
    const form = document.querySelector('[data-bald-review-form]');
    if (!form) return;

    const endpoint = form.dataset.endpoint;
    const status = form.querySelector('[data-bald-review-status]');
    const button = form.querySelector('[data-bald-review-submit]');
    const body = form.querySelector('textarea[name="body"]');
    const count = form.querySelector('[data-bald-count]');

    /* Nobody reads the page, picks a rating and writes twenty characters in
       three seconds. The server checks this too. */
    const openedAt = Date.now();

    if (body && count) {
      const max = body.getAttribute('maxlength') || '1200';
      const sync = () => {
        count.textContent = body.value.length + ' / ' + max;
      };
      body.addEventListener('input', sync);
      sync();
    }

    const say = (key) => {
      if (!status) return;
      status.textContent = status.dataset[key] || status.dataset.success || '';
      status.classList.toggle('is-error', key.indexOf('success') !== 0);
      status.classList.add('is-shown');
    };

    form.addEventListener('submit', async (event) => {
      event.preventDefault();

      if (!form.checkValidity()) {
        form.reportValidity();
        say('invalid');
        return;
      }

      const data = new FormData(form);
      const payload = {
        rating: Number(data.get('rating') || 0),
        author: (data.get('author') || '').trim(),
        city: (data.get('city') || '').trim(),
        body: (data.get('body') || '').trim(),
        /* No e-mail, no order number. The product is the page we are on, and
           who the shopper is comes from Shopify, not from this form. */
        product_gid: form.dataset.productGid || '',
        website: data.get('website') || '',
        elapsed: Date.now() - openedAt,
      };

      button.disabled = true;
      button.classList.add('is-busy');

      try {
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify(payload),
        });

        if (response.status === 429) {
          say('throttled');
        } else if (response.ok) {
          let verified = false;
          try {
            verified = (await response.json()).verified === true;
          } catch (e) {
            /* A success without readable JSON is still a success. */
          }
          form.reset();
          if (count && body) count.textContent = '0 / ' + (body.getAttribute('maxlength') || '1200');
          say(verified ? 'successVerified' : 'success');
          form.classList.add('is-sent');
        } else {
          say(response.status === 400 ? 'invalid' : 'error');
        }
      } catch (e) {
        /* Service down or no network. Never leave a dead button behind. */
        say('error');
      } finally {
        button.disabled = false;
        button.classList.remove('is-busy');
      }
    });
  }

  /* The page has already drawn Shopify's copy of the reviews by the time this
     runs, so this can only ever ADD. If the request fails, or the service is
     down, the visitor keeps exactly what they were already looking at.

     It exists for one narrow case: a review that was approved but whose copy
     into Shopify failed, which would otherwise stay invisible until the next
     scheduled sync. */
  /* A review's day as the shop sees it: Liquid's date filter works in the
     shop's time zone, so this does too rather than in the visitor's. Returns
     "30. 9. 2026" for people, or "2026-09-30" for a <time> element. */
  const SHOP_TIME_ZONE = 'Europe/Bratislava';

  function reviewDay(date, form) {
    let d = date.getDate();
    let m = date.getMonth() + 1;
    let y = date.getFullYear();
    try {
      const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: SHOP_TIME_ZONE,
        day: 'numeric',
        month: 'numeric',
        year: 'numeric',
      }).formatToParts(date);
      const part = (type) => Number(parts.find((p) => p.type === type).value);
      d = part('day');
      m = part('month');
      y = part('year');
    } catch (e) {
      /* No time-zone support: the visitor's own clock, a day out at worst. */
    }
    if (form === 'iso') return y + '-' + String(m).padStart(2, '0') + '-' + String(d).padStart(2, '0');
    return d + '. ' + m + '. ' + y;
  }

  function initReviewTopUp() {
    const grid = document.querySelector('[data-bald-reviews-grid]');
    if (!grid || !grid.dataset.productGid) return;
    if (!('fetch' in window)) return;

    const url =
      (grid.dataset.proxy || '/apps/recenzie') +
      '/reviews?product_gid=' +
      encodeURIComponent(grid.dataset.productGid);

    const known = new Set(
      Array.from(grid.querySelectorAll('[data-review-id]'))
        .map((el) => el.dataset.reviewId)
        .filter(Boolean)
    );

    const stars = (n) => {
      let out = '';
      for (let i = 1; i <= 5; i++) out += i <= n ? '★' : '☆';
      return out;
    };

    window.setTimeout(async () => {
      let data;
      try {
        const response = await fetch(url, { credentials: 'same-origin' });
        if (!response.ok) return;
        data = await response.json();
      } catch (e) {
        return; // silent by design
      }
      if (!data || !Array.isArray(data.reviews)) return;

      const missing = data.reviews.filter((r) => r.id && !known.has(r.id));
      if (!missing.length) return;

      missing.forEach((review) => {
        const card = document.createElement('article');
        card.className = 'bald-reviews__card';
        card.dataset.reviewId = review.id;

        const top = document.createElement('div');
        top.className = 'bald-reviews__top';

        const rated = document.createElement('span');
        rated.className = 'bald-reviews__stars';
        rated.setAttribute('aria-label', review.rating + ' z 5 hviezdičiek');
        rated.textContent = stars(review.rating);
        top.appendChild(rated);

        if (review.verified) {
          const badge = document.createElement('span');
          badge.className = 'bald-reviews__verified';
          badge.textContent = 'Overený nákup';
          top.appendChild(badge);
        }

        /* The same date the Liquid card shows, in the same form ("30. 9.
           2026") and the shop's time zone, so a card added here cannot read a
           day apart from one rendered by the server. */
        const when = review.submitted_at ? new Date(review.submitted_at) : null;
        if (when && !isNaN(when)) {
          const time = document.createElement('time');
          time.className = 'bald-reviews__date';
          time.dateTime = reviewDay(when, 'iso');
          time.textContent = reviewDay(when, 'sk');
          top.appendChild(time);
        }

        const text = document.createElement('p');
        text.className = 'bald-reviews__text';
        /* textContent, not innerHTML. This is text a stranger wrote. */
        text.textContent = review.body;

        const who = document.createElement('p');
        who.className = 'bald-reviews__who';
        who.textContent = review.city ? review.author + ', ' + review.city : review.author;

        card.appendChild(top);
        card.appendChild(text);
        card.appendChild(who);
        grid.appendChild(card);
      });

      /* Tell the pager the count changed so it can re-page and re-hide. */
      document.dispatchEvent(new CustomEvent('bald:reviews-added'));
    }, 400);
  }


  /* ---------------------------------------------------------------- */
  /* Buy box: keep the price on the button in step with the pack       */
  /* ---------------------------------------------------------------- */

  /* Every price is already formatted by Liquid and parked on the radio, so
     this never has to know about currency symbols or decimal commas. */

  /* Liquid formats every price that is known at render time. The free-shipping
     remainder is not one of them any more — it follows the cart, which changes
     after the page is built — so this formats it the same way Shopify would,
     from the shop's own money_format string. Only the four placeholders
     Shopify actually uses are handled. */
  function baldFormatMoney(cents, format) {
    const value = Math.max(0, Math.round(Number(cents) || 0));
    const pattern = format || '${{amount}}';

    const withDelimiters = (decimals, thousands, decimalMark) => {
      const fixed = (value / 100).toFixed(decimals);
      const [whole, part] = fixed.split('.');
      const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return part ? grouped + decimalMark + part : grouped;
    };

    /* The amount and "€" are joined by a no-break space (2026-10-21): the
       shop's format has a plain one, and on a narrow phone "16,00" ended a
       line with "€" alone on the next. Liquid does the same with a replace
       after every | money in the bald-* files and the cart panel. */
    return pattern
      .replace(/\{\{\s*(\w+)\s*\}\}/, (_, name) => {
        switch (name) {
          case 'amount_no_decimals':
            return withDelimiters(0, ',', '.');
          case 'amount_with_comma_separator':
            return withDelimiters(2, '.', ',');
          case 'amount_no_decimals_with_comma_separator':
            return withDelimiters(0, '.', ',');
          default:
            return withDelimiters(2, ',', '.');
        }
      })
      .replace(/ /g, ' ');
  }

  function initBuyCard() {
    const form = document.querySelector('[data-bald-buy]');
    if (!form) return;

    const priceOut = form.querySelector('[data-bald-buy-price]');
    const submit = form.querySelector('[data-bald-buy-submit]');
    const shipProgress = form.querySelector('[data-bald-ship-progress]');
    const shipDone = form.querySelector('[data-bald-ship-done]');
    const remainingOut = form.querySelector('[data-bald-buy-remaining]');
    const radios = form.querySelectorAll('.bald-packs__radio');
    if (!radios.length) return;

    /* Either "the cart still needs X" or "it is there". Liquid renders the
       right one from cart.total_price, so this only has to keep up as the cart
       changes. */
    const shipping = form.querySelector('[data-bald-buy-shipping]');
    const threshold = Number((shipping && shipping.dataset.freeFrom) || 0);
    const moneyFormat = shipping ? shipping.dataset.moneyFormat : '';

    const showShipping = (remainingCents) => {
      if (!shipProgress || !shipDone) return;
      const short = remainingCents > 0;
      if (short && remainingOut) remainingOut.textContent = baldFormatMoney(remainingCents, moneyFormat);
      shipProgress.hidden = !short;
      shipDone.hidden = short;
    };

    /* One request, and only when there is a threshold to measure against.

       The sequence guard matters because two cart changes in quick succession
       start two reads, and they can come back out of order — the older answer
       would then overwrite the newer one and leave a number that does not match
       the cart until something else happens. */
    let shippingRead = 0;
    const refreshShipping = async () => {
      if (!threshold || !shipProgress) return;
      const mine = ++shippingRead;
      try {
        const cart = await fetch(baldCartRoot() + 'cart.js', { headers: { Accept: 'application/json' } })
          .then((r) => r.json());
        if (mine !== shippingRead) return;
        showShipping(threshold - cart.total_price);
      } catch (e) {
        /* Leave whatever Liquid rendered; a stale line beats a blank one. */
        console.log('shipping line refresh failed:', e && e.message);
      }
    };

    const sync = () => {
      const chosen = form.querySelector('.bald-packs__radio:checked');

      /* Every pack sold out: say so on the button rather than letting someone
         click through to an error. */
      if (!chosen) {
        if (submit) {
          submit.disabled = true;
          const label = submit.querySelector('.bald-buy-bar__label');
          if (label) label.textContent = submit.dataset.soldoutLabel || 'Vypredané';
        }
        if (priceOut) priceOut.textContent = '';
        return;
      }

      if (submit) submit.disabled = false;
      if (priceOut) priceOut.textContent = chosen.dataset.price || '';

      /* Deliberately does not touch the shipping line. Choosing a pack puts
         nothing in the cart, so the remainder cannot have changed. */
    };

    radios.forEach((radio) => radio.addEventListener('change', sync));

    /* Two sources, because the cart changes two ways.

       Sense publishes cartUpdate from product-form.js, which covers anything
       added through the theme's own form. The buy button here does NOT go
       through that — initPackCart intercepts the click so it can consolidate
       packs, and calls baldAddToCart directly — so that path raises its own
       event. Subscribing to only the first is why the line sat at the full
       threshold until a reload. */
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, refreshShipping);
    }
    document.addEventListener('bald:cart-changed', refreshShipping);

    /* Coming back to a page with a cart already filled: bfcache restores the
       old HTML, so re-read rather than trust it. */
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) refreshShipping();
    });

    /* product-form.js runs `this.variantIdInput.disabled = false` in its
       constructor, and variantIdInput is the FIRST [name=id] it finds — which
       here is a radio. If the first pack happens to be sold out, that quietly
       re-enables it. Put the disabled state back after the element upgrades. */
    window.requestAnimationFrame(() => {
      radios.forEach((radio) => {
        const card = radio.closest('.bald-packs__item');
        if (card && card.classList.contains('is-soldout')) radio.disabled = true;
      });
      sync();
    });

    sync();
  }

  /* ---------------------------------------------------------------- */
  /* Cart: keep packs consolidated                                     */
  /* ---------------------------------------------------------------- */

  /* Packs are how this shop expresses quantity, so two singles in the cart is
     the same goods as one two-pack but at a higher price. Adding a single to a
     cart that already holds one now becomes the two-pack, which is what the
     shopper meant and is cheaper for them.

     Only ever consolidates when the result costs the same or less. It will not
     quietly move anyone onto a more expensive line. */

  function baldCartRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  /* Adds a variant and repaints the drawer from the section HTML Shopify sends
     back, which is how the theme's own product form does it. */
  /* Anything that changes the cart outside Sense's own product form says so
     here, so the free-shipping line (and anything else that cares) can catch
     up without every caller having to know who is listening. */
  function baldCartChanged() {
    document.dispatchEvent(new CustomEvent('bald:cart-changed'));
  }

  /* Takes either one variant id, or a list of {id, quantity}. The list form
     matters: rebuilding a cart as two three-packs in a single request means one
     round trip, one drawer render and one announcement, instead of the drawer
     redrawing once per pack. */
  async function baldAddToCart(idOrItems, quantity) {
    const drawer = document.querySelector('cart-drawer');
    const items = Array.isArray(idOrItems)
      ? idOrItems.map((i) => ({ id: Number(i.id), quantity: i.quantity || 1 }))
      : [{ id: Number(idOrItems), quantity: quantity || 1 }];
    const body = { items };

    if (drawer && typeof drawer.getSectionsToRender === 'function') {
      body.sections = drawer.getSectionsToRender().map((section) => section.id);
      body.sections_url = window.location.pathname;
    }

    const response = await fetch(baldCartRoot() + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(body),
    });
    const parsed = await response.json();

    if (parsed.status) throw new Error(parsed.description || parsed.message || 'add failed');

    if (drawer && parsed.sections) {
      drawer.renderContents(parsed);
      /* renderContents only clears is-empty from .drawer__inner. The class also
         sits on the <cart-drawer> element itself, and the theme's own form
         clears that separately — without it the drawer still styles itself as
         an empty cart after the first add. */
      drawer.classList.remove('is-empty');
    }

    baldCartChanged();
    return parsed;
  }

  /* announce: false for a remove that is one step of a larger change.
     Consolidating two singles into a two-pack removes the old line and then
     adds the new one; announcing the remove let anything listening read a
     momentarily empty cart, which is why the free-shipping line flashed back
     to the full threshold before settling. The caller announces once at the
     end instead. */
  async function baldRemoveLine(key, options) {
    await fetch(baldCartRoot() + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: 0 }),
    });

    if (!options || options.announce !== false) baldCartChanged();
  }

  /* The pack arithmetic, shared by the buy box (initPackCart) and the cart's
     own +/− (initCartPacks), so both follow exactly one rule. `packs` is the
     JSON both of them print: { product_id, variants: [{ id, units, price,
     available }] }. */
  function packMath(packs) {
    const byId = new Map(packs.variants.map((v) => [Number(v.id), v]));
    const unitsOf = (id) => (byId.get(Number(id)) || {}).units || 0;
    const priceOf = (id) => (byId.get(Number(id)) || {}).price || 0;

    /* The cheapest way to buy exactly this many units, allowing more than one
       of a pack.

       The old version looked for a single variant whose units matched the
       total, which only ever worked for small carts. Three units plus two plus
       one is six, no pack contains six, so it gave up and left three separate
       lines at 99,70 — when two three-packs is the same six units for 89,80.

       This is the coin-change problem and the numbers here are tiny, so it is
       solved exactly rather than approximated: best[u] is the cheapest cost to
       reach exactly u units, built up one unit at a time. Sold-out packs are
       left out, so it can only ever propose something buyable. Returns null
       when no combination hits the total exactly — with a one-unit pack in the
       range that cannot happen, but it must not guess if the range changes. */
    const cheapestMix = (units) => {
      const available = packs.variants.filter((v) => v.available && v.units > 0);
      if (!available.length || units <= 0) return null;

      const best = new Array(units + 1).fill(null);
      best[0] = { cost: 0, use: null, from: -1 };

      for (let u = 1; u <= units; u++) {
        for (const v of available) {
          if (v.units > u) continue;
          const prev = best[u - v.units];
          if (!prev) continue;
          const cost = prev.cost + v.price;
          if (!best[u] || cost < best[u].cost) best[u] = { cost, use: v, from: u - v.units };
        }
      }
      if (!best[units]) return null;

      const counts = new Map();
      for (let u = units; u > 0; u = best[u].from) {
        const id = Number(best[u].use.id);
        counts.set(id, (counts.get(id) || 0) + 1);
      }
      return { cost: best[units].cost, items: [...counts].map(([id, quantity]) => ({ id, quantity })) };
    };

    /* Two baskets hold the same thing when every pack appears the same number
       of times. Used to tell "the tidy answer is what a plain add would give
       anyway" from "the cart is worth rebuilding". */
    const sameBasket = (a, b) => {
      if (a.length !== b.length) return false;
      const key = (list) => list.map((i) => Number(i.id) + 'x' + i.quantity).sort().join('|');
      return key(a) === key(b);
    };

    return { unitsOf, priceOf, cheapestMix, sameBasket };
  }

  function readPacks(el) {
    if (!el) return null;
    try {
      const packs = JSON.parse(el.textContent);
      return packs && Array.isArray(packs.variants) ? packs : null;
    } catch (e) {
      return null;
    }
  }

  function initPackCart() {
    const form = document.querySelector('[data-bald-buy]');
    const packs = readPacks(document.querySelector('[data-bald-pack-map]'));
    if (!packs || !form) return; // leave the theme's normal add-to-cart alone
    const { unitsOf, priceOf, cheapestMix, sameBasket } = packMath(packs);

    const submit = form.querySelector('[data-bald-buy-submit]');
    if (!submit) return;

    /* Captured on the document so this runs before the theme's own submit
       handler ever sees it; preventing the click stops the form submitting. */
    document.addEventListener(
      'click',
      async (event) => {
        const button = event.target.closest('[data-bald-buy-submit]');
        if (!button || button !== submit) return;

        event.preventDefault();
        event.stopPropagation();

        const chosen = form.querySelector('.bald-packs__radio:checked');
        if (!chosen || button.disabled) return;

        const wantId = Number(chosen.value);
        button.classList.add('loading');
        const spinner = button.querySelector('.loading__spinner');
        if (spinner) spinner.classList.remove('hidden');

        try {
          const cart = await fetch(baldCartRoot() + 'cart.js').then((r) => r.json());
          const mine = (cart.items || []).filter((item) => item.product_id === packs.product_id);
          const existingUnits = mine.reduce((n, item) => n + unitsOf(item.variant_id) * item.quantity, 0);

          const totalUnits = existingUnits + unitsOf(wantId);

          /* What a plain add would leave behind, and what it would cost. */
          const naiveItems = mine.map((item) => ({ id: Number(item.variant_id), quantity: item.quantity }));
          const existing = naiveItems.find((i) => i.id === wantId);
          if (existing) existing.quantity += 1;
          else naiveItems.push({ id: wantId, quantity: 1 });
          const naiveCost = mine.reduce((n, item) => n + priceOf(item.variant_id) * item.quantity, 0) + priceOf(wantId);

          const mix = cheapestMix(totalUnits);

          /* Rebuild only when it is the same goods, genuinely different from
             what a plain add gives, and never more expensive. */
          const worthRebuilding =
            existingUnits > 0 && mix && !sameBasket(mix.items, naiveItems) && mix.cost <= naiveCost;

          if (worthRebuilding) {
            /* The removes stay quiet so nothing reads the cart while it is
               half-rebuilt; the single add at the end is what announces. */
            for (const item of mine) await baldRemoveLine(item.key, { announce: false });
            await baldAddToCart(mix.items);
          } else {
            await baldAddToCart(wantId, 1);
          }
        } catch (e) {
          /* Anything unexpected: fall back to a plain add so the shopper is
             never left with a dead button. */
          try {
            await baldAddToCart(wantId, 1);
          } catch (e2) {
            window.location.href = baldCartRoot() + 'cart';
          }
        } finally {
          button.classList.remove('loading');
          if (spinner) spinner.classList.add('hidden');
        }
      },
      true
    );
  }

  /* The same rule for changes made in the cart itself: its +/− and the bin.
     (Samuel, 2026-10-04: the cart shows "Balenie: 3 kusy" and a stepper, and
     "everything else stays the same, including giving them the best balenie
     option".) So + on "1 kus" ends as one "2 kusy", which is cheaper than two
     singles, and + on "2 kusy" as "3 kusy" and "1 kus". + on "3 kusy" is
     simply two of them, already the cheapest.

     Every change in the cart goes through Sense's CartItems.updateQuantity,
     so that one method is wrapped. Before Sense posts the change, the wrapper
     works out the cart that change would leave; if the same number of pieces
     costs less as a different mix of packs, it sets the cart to that mix
     instead, in one /cart/update.js call (variant ids, 0 for the packs that go)
     which also returns the redrawn cart. Otherwise it hands over to Sense
     untouched. The rule is the buy box's, with one difference: here a rebuild
     needs a strictly lower price, so a mix the shopper arranged is never
     reshuffled for nothing.

     It redraws the way Sense's own quantity change does, not through
     renderContents: that is the add path, and on a phone it would retitle the
     panel "Produkt pridaný" for a click on +. */
  function initCartPacks() {
    if (!window.customElements) return;
    customElements.whenDefined('cart-drawer-items').then(() => {
      const Items = customElements.get('cart-drawer-items');
      const plain = Items && Items.prototype.updateQuantity;
      if (typeof plain !== 'function' || plain.baldPacks) return;

      // What the change would leave, and the cheaper mix if there is one.
      const plan = async (line, quantity) => {
        const packs = readPacks(document.querySelector('[data-bald-cart-packs]'));
        if (!packs) return null;
        const { unitsOf, priceOf, cheapestMix, sameBasket } = packMath(packs);
        const cart = await fetch(baldCartRoot() + 'cart.js').then((r) => r.json());
        const items = cart.items || [];
        const target = items[line - 1];
        if (!target || target.product_id !== packs.product_id) return null;

        const after = items
          .filter((item) => item.product_id === packs.product_id)
          .map((item) => ({ id: Number(item.variant_id), quantity: item === target ? quantity : item.quantity }))
          .filter((i) => i.quantity > 0);
        const units = after.reduce((n, i) => n + unitsOf(i.id) * i.quantity, 0);
        if (units <= 0) return null;
        const cost = after.reduce((n, i) => n + priceOf(i.id) * i.quantity, 0);

        const mix = cheapestMix(units);
        if (!mix || sameBasket(mix.items, after) || mix.cost >= cost) return null;

        const updates = {};
        packs.variants.forEach((v) => { updates[Number(v.id)] = 0; });
        mix.items.forEach((i) => { updates[i.id] = i.quantity; });
        return updates;
      };

      function updateQuantity(line, quantity, event, name, variantId) {
        const self = this;
        const args = arguments;
        self.enableLoading(line);

        plan(Number(line), Number(quantity))
          .then(async (updates) => {
            if (!updates) {
              self.disableLoading(line);
              return plain.apply(self, args);
            }
            const sections = self.getSectionsToRender();
            const parsed = await fetch(baldCartRoot() + 'cart/update.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
              body: JSON.stringify({
                updates,
                sections: sections.map((s) => s.section),
                sections_url: window.location.pathname,
              }),
            }).then((r) => r.json());
            if (parsed.status || !parsed.sections) throw new Error(parsed.description || 'update failed');

            // Exactly what Sense does after its own change.
            const drawer = document.querySelector('cart-drawer');
            self.classList.toggle('is-empty', parsed.item_count === 0);
            if (drawer) drawer.classList.toggle('is-empty', parsed.item_count === 0);
            sections.forEach((s) => {
              const host = document.getElementById(s.id);
              if (!host) return;
              const target = host.querySelector(s.selector) || host;
              target.innerHTML = self.getSectionInnerHTML(parsed.sections[s.section], s.selector);
            });
            self.disableLoading(line);
            const first = drawer && drawer.querySelector('.cart-item__name');
            if (drawer && first && typeof trapFocus === 'function') trapFocus(drawer, first);
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsed, variantId });
            } else {
              baldCartChanged();
            }
          })
          .catch(() => {
            // Anything unexpected: Sense's plain change, as if this were not here.
            self.disableLoading(line);
            plain.apply(self, args);
          });
      }
      updateQuantity.baldPacks = true;
      Items.prototype.updateQuantity = updateQuantity;
    });
  }

  /* ---------------------------------------------------------------- */
  /* Cart panel: hang it off the cart icon, title it by how it opened   */
  /* ---------------------------------------------------------------- */

  /* Sense ships one slide-in drawer. The CSS turns it into a dropdown on
     desktop and a centred card on phones; this supplies the two things CSS
     cannot work out on its own:

       - where the cart icon actually is, so the dropdown lines up with it
       - whether the panel was opened by adding something, which is what
         makes the phone version say "Produkt pridaný" */


  /* ---------------------------------------------------------------- */
  /* Problems carousel — phones only                                   */
  /* ---------------------------------------------------------------- */

  /* Four stacked cards is four screens of scrolling on a phone. Below 750px
     the CSS turns the grid into a snapping horizontal scroller and this makes
     it loop and advance on its own.

     Native scroll does the moving, not a transform. That is what keeps the
     swipe feeling like the platform's own — momentum, rubber-banding and
     snapping all come free, and the thing stays usable if this script never
     runs at all.

     The theme editor re-renders a section in place whenever one of its
     settings changes — uploading a photo into a card, say. That swaps in a
     fresh strip and used to leave this script driving the old one, so a phone
     preview went dead until a full reload. Hence one mount per strip, rebuilt
     when the editor reloads the section. */

  function initProblemCarousel() {
    let destroy = mountProblemCarousel(document);
    if (!(window.Shopify && window.Shopify.designMode)) return;

    const ours = (e) => e.target.querySelector && e.target.querySelector('[data-bald-problems-track]');
    document.addEventListener('shopify:section:unload', (e) => {
      if (destroy && ours(e)) { destroy(); destroy = null; }
    });
    document.addEventListener('shopify:section:load', (e) => {
      if (!ours(e)) return;
      if (destroy) destroy();
      destroy = mountProblemCarousel(e.target);
    });
  }

  function mountProblemCarousel(root) {
    const track = root.querySelector('[data-bald-problems-track]');
    const dotsBox = root.querySelector('[data-bald-problems-dots]');
    if (!track) return null;

    const phone = window.matchMedia('(max-width: 749px)');
    const still = window.matchMedia('(prefers-reduced-motion: reduce)');

    const ADVANCE_MS = 3500;
    /* Long enough to read the first card before it moves under you, short
       enough that it is obvious the strip is going somewhere. Applied when the
       section comes into view, not on page load — the timer would otherwise
       have run its course before the reader ever got here. */
    const START_MS = 2000;
    /* Long enough that a deliberate swipe is not immediately fought by the
       timer starting up again. */
    const RESUME_MS = 9000;

    const RISE = 'bald-problems__card--enter';
    const SENSE = ['scroll-trigger', 'scroll-trigger--offscreen', 'animate--slide-in'];

    let cards = [];
    let clones = [];
    let inside = new Set();
    let timer = null;
    let starting = null;
    let resume = null;
    let settle = null;
    let seen = null;
    let active = false;
    let visible = false;
    let primed = false;

    const real = () => Array.from(track.children).filter((n) => !n.dataset.baldClone);

    /* --- Sense's reveal ----------------------------------------------
       Sense's reveal classes come off the cards while this is a carousel. Its
       observer reveals an element once, the first time it enters the
       viewport, and from then on .scroll-trigger:not(.scroll-trigger--offscreen)
       .animate--slide-in — specificity (0,3,0) — holds the animation property,
       so the per-card rise further down could never run.

       Except on the first card, for a while. That is the card on screen when
       the section arrives, and Sense's reveal is what makes it rise along with
       the heading above it. Stripping it at load, as this once did, left it
       sitting there while the heading rose. Its classes come off once that
       reveal has played, or the moment the card leaves the strip, whichever
       comes first. teardown puts them all back. */
    const unreveal = (card) => {
      card.removeEventListener('animationend', onRevealed);
      card.classList.remove(...SENSE);
    };

    /* Only a reveal that played on screen counts. At load, before Sense's
       observer has marked the card off-screen, its CSS already applies the
       slide-in to everything not yet marked, so the card starts animating
       while it is still far down the page. On a slow connection that
       animation can end just after this listener goes on, and taking it as
       "revealed" stripped the classes before the section was ever seen: the
       card then sat there while the heading rose (problems-rise caught it on
       the live site, one load in three). */
    const onRevealed = (e) => {
      if (e.target !== e.currentTarget) return;
      const r = e.currentTarget.getBoundingClientRect();
      if (r.top >= window.innerHeight || r.bottom <= 0) return;
      unreveal(e.currentTarget);
    };

    /* --- looping ---------------------------------------------------
       The last card is copied to the front and the first to the end. Scroll
       past either end and the position is reset to its twin with the jump
       hidden behind an instant scroll, so the strip feels endless in both
       directions. A transform-based carousel would need none of this, but it
       would also need its own touch handling. */
    const build = () => {
      cards = real();
      if (cards.length < 2) return false;

      const first = cards[0].cloneNode(true);
      const last = cards[cards.length - 1].cloneNode(true);
      [first, last].forEach((c) => {
        c.dataset.baldClone = 'true';
        c.setAttribute('aria-hidden', 'true');
        c.classList.remove(...SENSE, RISE);
        /* The theme editor finds a block by this attribute. On a copy it
           would highlight the clone instead of the card being edited. */
        c.removeAttribute('data-shopify-editor-block');
        c.querySelectorAll('[id]').forEach((n) => n.removeAttribute('id'));
      });
      track.insertBefore(last, cards[0]);
      track.appendChild(first);
      clones = [last, first];

      cards.slice(1).forEach((c) => c.classList.remove(...SENSE));
      cards[0].addEventListener('animationend', onRevealed);

      if (dotsBox) {
        dotsBox.innerHTML = '';
        cards.forEach((_, i) => {
          const b = document.createElement('button');
          b.type = 'button';
          b.className = 'bald-problems__dot';
          b.setAttribute('aria-label', 'Zobraziť ' + (i + 1) + '. z ' + cards.length);
          b.addEventListener('click', () => { hold(); go(i); schedule(RESUME_MS); });
          dotsBox.appendChild(b);
        });
        dotsBox.hidden = false;
      }
      return true;
    };

    const teardown = () => {
      if (!active) return;
      hold();
      clearTimeout(settle);
      listen(false);
      if (seen) { seen.disconnect(); seen = null; }
      visible = false;
      cards[0].removeEventListener('animationend', onRevealed);
      Array.from(track.children).forEach((c) => c.classList.remove(RISE));
      inside = new Set();
      /* Desktop gets Sense's reveal back, since there the cards really do
         enter the viewport from below. */
      cards.forEach((c) => c.classList.add('scroll-trigger', 'animate--slide-in'));
      clones.forEach((c) => c.remove());
      clones = [];
      if (dotsBox) { dotsBox.innerHTML = ''; dotsBox.hidden = true; }
      track.scrollLeft = 0;
      active = false;
    };

    /* One card plus the gap, measured rather than assumed. The gap is 1.6rem,
       which is 16px only while the theme's type scale stays at 100%, and a
       phone with a fractional viewport width gets a fractional card that
       clientWidth would round. */
    const step = () => cards[1].getBoundingClientRect().left - cards[0].getBoundingClientRect().left;

    const index = () => Math.round(track.scrollLeft / step()) - 1;

    const go = (i, smooth = true) => {
      track.scrollTo({ left: (i + 1) * step(), behavior: smooth && !still.matches ? 'smooth' : 'auto' });
    };

    const paint = () => {
      if (!dotsBox) return;
      let i = index();
      if (i < 0) i = cards.length - 1;
      if (i >= cards.length) i = 0;
      Array.from(dotsBox.children).forEach((d, n) => d.setAttribute('aria-current', String(n === i)));
    };

    /* --- the rise --------------------------------------------------
       Each card rises as it slides in, starting the moment its edge reaches
       the strip.

       It used to be replayed on scrollend, once the card had landed. By then
       the card had travelled the whole way in at full strength, so the reader
       watched it arrive, vanish and fade back up — a blink on every advance.
       Starting as the card enters makes the slide and the rise one movement.

       Worked out from the scroll position on every scroll event, straight
       away rather than on the next frame, so the class is on before the frame
       that would first show the card. The lookahead is half the gap: enough
       to catch a card before its first sliver shows, never enough to count a
       neighbour sitting still beside the strip. A card that leaves loses the
       class, so it rises again next time round — the clones too, since they
       are what the reader sees at the seam.

       Restarting a CSS animation needs the reflow between removing the class
       and adding it back; without it the browser coalesces the two changes
       and nothing happens. */
    const within = () => {
      const s = step();
      const w = track.getBoundingClientRect().width;
      const ahead = (s - w) / 2;
      const x = track.scrollLeft;
      return new Set(Array.from(track.children).filter((c, k) => {
        const left = k * s - x;
        return left < w + ahead && left + w > -ahead;
      }));
    };

    const rise = (card) => {
      card.classList.remove(RISE);
      void card.offsetWidth;
      card.classList.add(RISE);
    };

    const leave = (card) => {
      card.classList.remove(RISE);
      if (card === cards[0]) unreveal(card);
    };

    const enter = () => {
      const now = within();
      now.forEach((c) => { if (!inside.has(c)) rise(c); });
      inside.forEach((c) => { if (!now.has(c)) leave(c); });
      inside = now;
    };

    /* At the seam a clone is swapped for the card it copies. This gives the
       card whatever the clone is showing — the rise included, mid-flight if
       need be — so the swap cannot be seen. */
    const handOff = (from, to) => {
      if (!from.classList.contains(RISE)) return;
      to.classList.add(RISE);
      if (typeof from.getAnimations !== 'function') return;
      const a = from.getAnimations()[0];
      const b = to.getAnimations()[0];
      if (a && b) b.currentTime = a.currentTime;
    };

    /* Landing on a clone means we ran off an end; hop to its twin. The clone
       is a pixel copy of the card it stands in for, so moving the scroll
       position by exactly one strip length changes nothing on screen.

       Timing is the whole trick here, and it has been wrong twice.

       Waiting 120ms after the last scroll event was too late: momentum kept
       restarting the wait, so the ends felt stuck.

       Doing it mid-scroll was worse. An autoplay advance is a smooth scrollTo
       with an animation in flight; moving scrollLeft underneath it does not
       cancel that animation, it just relocates the start — so the browser
       carried on travelling to the old target from the new position and slid
       the whole strip back across. That is the lag-then-rewind.

       It now runs on scrollend, which fires once — after momentum has died and
       after a smooth scrollTo has arrived. Nothing is moving at that instant,
       so assigning scrollLeft is invisible, and because the event is immediate
       rather than a guessed delay there is no wait before the next swipe.

       The cards on screen afterwards are the twins of the ones on screen
       before. Each takes over its twin's rise and is recorded as already
       inside, so the scroll event this assignment raises does not start a
       rise of its own — that would be the blink again, at the seam. */
    const wrap = () => {
      const s = step();
      const span = cards.length * s;
      let shift = 0;
      if (track.scrollLeft < s * 0.5) shift = span;
      else if (track.scrollLeft > span + s * 0.5) shift = -span;
      if (!shift) return false;

      track.scrollLeft += shift;

      const kids = Array.from(track.children);
      const now = within();
      now.forEach((c) => {
        if (inside.has(c)) return;
        const twin = kids[kids.indexOf(c) + (shift > 0 ? -cards.length : cards.length)];
        if (twin) handOff(twin, c);
      });
      inside.forEach((c) => { if (!now.has(c)) leave(c); });
      inside = now;
      return true;
    };

    const next = () => {
      const i = index();
      go(i + 1);
    };

    /* --- autoplay -------------------------------------------------- */
    const stop = () => {
      clearInterval(timer);
      timer = null;
      clearTimeout(starting);
      starting = null;
    };

    /* stop, and cancel a pending resume as well */
    const hold = () => {
      stop();
      clearTimeout(resume);
      resume = null;
    };

    /* Reduce Motion used to stop this dead, which on an iPhone with that
       setting on — a lot of them — left the section looking broken: one card,
       never moving, three dots that did nothing.

       It still advances. What Reduce Motion changes is how: go() jumps
       straight to the next card instead of animating across to it, so there
       is no sliding motion to trigger anyone. Touching the strip pauses it,
       and the dots are always there to drive it by hand. */
    const schedule = (delay) => {
      stop();
      starting = setTimeout(() => {
        starting = null;
        next();
        timer = setInterval(() => {
          if (document.hidden) return;
          next();
        }, ADVANCE_MS);
      }, typeof delay === 'number' ? delay : ADVANCE_MS);
    };

    /* A swipe, a tap on a dot or a tab into the strip all mean the reader is
       driving. Give them a clear run before taking over again — and only take
       over if the section is still on screen. This used to restart
       regardless, so touching the strip and then scrolling on down the page
       left it ticking away out of sight. */
    const handOver = () => {
      hold();
      resume = setTimeout(() => {
        resume = null;
        if (visible) schedule(ADVANCE_MS);
      }, RESUME_MS);
    };

    /* Hover pauses it for a mouse only. A tap on a phone fires compatibility
       mouse events as well, and those are the touch handlers' business. */
    const onPointerEnter = (e) => { if (e.pointerType === 'mouse') stop(); };
    const onPointerLeave = (e) => { if (e.pointerType === 'mouse' && visible) schedule(ADVANCE_MS); };

    /* The dots keep up with the finger, so they repaint on every frame of the
       scroll, and the rise bookkeeping runs on every scroll event. The wrap is
       the opposite and waits for scrollend. */
    let painting = false;
    const onScroll = () => {
      enter();
      if (!painting) {
        painting = true;
        requestAnimationFrame(() => { painting = false; paint(); });
      }
    };

    const onScrollEnd = () => {
      if (!active) return;
      wrap();
      paint();
    };

    /* scrollend is Chrome 114+ and Firefox 109+. Safari 18 still lacks it, so
       iPhones take the fallback: a short debounce — later than ideal at the
       seam, but correct. */
    const hasScrollEnd = 'onscrollend' in window;
    const onScrollFallback = () => {
      clearTimeout(settle);
      settle = setTimeout(onScrollEnd, 120);
    };

    /* Everything added here comes off again in teardown. It used to stay, so
       every rotation of the phone stacked up another set of listeners and
       another observer, which carried on running on the desktop layout. */
    const listen = (on) => {
      const method = on ? 'addEventListener' : 'removeEventListener';
      track[method]('scroll', onScroll, { passive: true });
      if (hasScrollEnd) track[method]('scrollend', onScrollEnd);
      else track[method]('scroll', onScrollFallback, { passive: true });
      track[method]('pointerdown', handOver);
      track[method]('touchstart', handOver, { passive: true });
      track[method]('focusin', handOver);
      track[method]('pointerenter', onPointerEnter);
      track[method]('pointerleave', onPointerLeave);
    };

    /* Card photos are loading="lazy". Safari does not count a lazy image in a
       card parked off to the side of the strip as near the viewport, so it
       only started downloading as its card slid in — a blank card arriving.
       Chrome looks a little way sideways, not the whole strip. Once the
       section starts to come into view, fetch them all. */
    const prime = () => {
      primed = true;
      track.querySelectorAll('img[loading="lazy"]').forEach((img) => { img.loading = 'eager'; });
    };

    /* Nothing should be ticking while the section is out of sight. The ratio
       decides rather than isIntersecting: Chrome and Safari both report
       isIntersecting against the threshold, but the spec defines it as any
       overlap at all, so a browser that follows the letter would start the
       timer on the first pixel of the strip. */
    const onSeen = (entries) => entries.forEach((e) => {
      if (e.isIntersecting && !primed) prime();
      const now = e.intersectionRatio >= 0.35;
      if (now === visible) return;
      visible = now;
      if (visible) schedule(START_MS);
      else hold();
    });

    const enable = () => {
      if (active) return;
      if (!build()) return;
      active = true;
      track.scrollTo({ left: step(), behavior: 'auto' });
      inside = within();
      paint();
      listen(true);

      if ('IntersectionObserver' in window) {
        seen = new IntersectionObserver(onSeen, { threshold: 0.35 });
        seen.observe(track);
      } else {
        visible = true;
        prime();
        schedule(START_MS);
      }
    };

    const onStill = () => { if (active && visible) schedule(ADVANCE_MS); };

    const sync = () => (phone.matches ? enable() : teardown());
    sync();
    phone.addEventListener('change', sync);
    still.addEventListener('change', onStill);

    return () => {
      phone.removeEventListener('change', sync);
      still.removeEventListener('change', onStill);
      teardown();
    };
  }

  function initCartPanel() {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;

    const icon = document.querySelector('#cart-icon-bubble, a[href$="/cart"]');
    const section = document.querySelector('.section-header');

    /* Measured rather than guessed: the header height changes with the logo
       across breakpoints, and the icon shifts with the page gutter.

       The header hides while the page scrolls down (on-scroll-up), and then
       the icon is above the window. Opening the cart brings the header back
       (bald-cart-open, below), so the panel is placed where the icon is
       about to be, not where it is mid-slide. */
    const place = () => {
      if (!icon) return;
      const rect = icon.getBoundingClientRect();
      let bottom = rect.bottom;
      if (section && section.classList.contains('shopify-section-header-hidden') && !section.classList.contains('bald-cart-open')) {
        bottom += section.offsetHeight;
      }
      const fromRight = Math.max(window.innerWidth - rect.right, 8);
      drawer.style.setProperty('--bald-cart-top', Math.round(bottom + 12) + 'px');
      drawer.style.setProperty('--bald-cart-right', Math.round(fromRight) + 'px');
      /* Centre the little pointer on the icon. */
      drawer.style.setProperty('--bald-cart-arrow', Math.round(rect.width / 2 - 7) + 'px');
    };

    /* Placed on open and on resize only. It used to re-measure on every
       scroll event for the whole visit, cart closed or not; while the cart is
       open the page cannot scroll anyway (Sense locks it). */
    place();
    window.addEventListener('resize', place);

    const setHeaderHeld = (held) => {
      if (section) section.classList.toggle('bald-cart-open', held);
    };

    const title = () => drawer.querySelector('[data-bald-cart-title-text]');

    /* The confirmation wording is a phone thing. On desktop the panel is the
       cart itself, dropped from the icon, so it stays titled "Košík". */
    const onPhone = () => window.matchMedia('(max-width: 749px)').matches;

    const setAdded = (added) => {
      drawer.classList.toggle('bald-cart--added', added);
      const t = title();
      if (t) t.textContent = added && onPhone() ? 'Produkt pridaný' : 'Košík';
    };

    /* renderContents runs on every add; open() also runs when the icon is
       clicked. Marking the add first and clearing it on a plain open is what
       tells the two apart. */
    const originalRender = drawer.renderContents && drawer.renderContents.bind(drawer);
    if (originalRender) {
      drawer.renderContents = (parsed) => {
        place();
        originalRender(parsed);
        setAdded(true);
        /* The cart count settles in (bald-style.css, .bald-bump). Added on the
           first add rather than at load so arriving on the page is quiet; the
           bubble is rebuilt on every render, so each later add replays it. */
        if (icon) icon.classList.add('bald-bump');
      };
    }

    if (icon) {
      icon.addEventListener('click', () => {
        place();
        setAdded(false);
      });
    }

    /* A change made in the cart itself (its +/− or the bin) means the shopper
       is managing the cart, not reading a confirmation. The redraw has already
       put "Košík" back in the title; this brings the count and total back on a
       phone to match. */
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (e) => {
        if (e && e.source === 'cart-items') setAdded(false);
      });
    }

    /* Anything else that opens it is a cart view, not a confirmation. Sense's
       own renderContents calls this.open(), so an add comes through here too. */
    const originalOpen = drawer.open && drawer.open.bind(drawer);
    if (originalOpen) {
      drawer.open = (triggeredBy) => {
        place();
        setHeaderHeld(true);
        originalOpen(triggeredBy);
      };
    }

    /* Every way the cart closes — the overlay, Escape, the close button —
       calls this.close(), so the header is released here. */
    const originalClose = drawer.close && drawer.close.bind(drawer);
    if (originalClose) {
      drawer.close = () => {
        originalClose();
        setHeaderHeld(false);
      };
    }
  }

  document.addEventListener('DOMContentLoaded', () => {
    initHeaderTint();
    initTextureBand();
    initStickyBar();
    initBuyCard();
    initProblemCarousel();
    initPackCart();
    initCartPacks();
    initAnchorScroll();
    initCookiePrefs();
    initCartPanel();
    initCartShippingBar();
    initCartUpsell();
    initLegalToc();
    initRteTables();
    initReviewModal();
    initReviewPager();
    initReviewForm();
    initReviewTopUp();
  });
})();
